import React, { useRef, useState } from 'react';
import { Upload, FileText, AlertTriangle } from 'lucide-react';
import Papa from 'papaparse';

const GenericImporterFileUploadStep = ({ config, onFileSelected, loading }) => {
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);

    // File upload handler
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        processFile(file);
    };

    // Process the selected file
    const processFile = (file) => {
        setError(null);

        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            setError('Endast CSV-filer stöds.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Filen är för stor. Max storlek är 5MB.');
            return;
        }

        setSelectedFile(file);

        // Parse CSV file
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.errors.length > 0) {
                    setError(`Fel vid parsning av filen: ${results.errors[0].message}`);
                    return;
                }

                // Pass the file and parsed data to parent component
                onFileSelected(file, results.data, results.meta.fields);
            },
            error: (error) => {
                setError(`Fel vid läsning av filen: ${error.message}`);
            }
        });
    };

    // Drag and drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    return (
        <>
            <style>{`
        .file-upload-container {
          border: 2px dashed #e2e8f0;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        
        .file-upload-active {
          border-color: #44814E;
          background: rgba(68, 129, 78, 0.05);
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
        }
        
        .upload-icon-container {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
        }
        
        .upload-icon {
          color: #44814E;
        }
        
        .upload-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }
        
        .upload-subtitle {
          color: #64748b;
          margin-bottom: 1.5rem;
        }
        
        .file-input {
          display: none;
        }
        
        .upload-btn {
          background: #44814E;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .upload-btn:hover {
          background: #36673e;
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .upload-btn:active {
          transform: translateY(0);
        }
        
        .selected-file {
          margin-top: 1.5rem;
          padding: 1rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .file-icon {
          background: #f1f5f9;
          padding: 0.5rem;
          border-radius: 6px;
          color: #44814E;
        }
        
        .file-details {
          flex: 1;
          text-align: left;
        }
        
        .file-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        
        .file-size {
          color: #64748b;
          font-size: 0.875rem;
        }
        
        .error-container {
          margin-top: 1.5rem;
          padding: 1rem;
          background: #fef2f2;
          border-radius: 8px;
          border: 1px solid #fee2e2;
          color: #dc2626;
          text-align: left;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .instructions-container {
          margin-top: 2.5rem;
          padding: 1.5rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          text-align: left;
        }
        
        .instructions-title {
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 1rem;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .instructions-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .instruction-item {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.5rem 0;
          color: #475569;
        }
        
        .instruction-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          color: #44814E;
          flex-shrink: 0;
        }
      `}</style>

            <div className="file-upload-step">
                <div
                    className={`file-upload-container ${dragActive ? 'file-upload-active' : ''}`}
                    onClick={handleClick}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <div className="upload-icon-container">
                        <Upload size={32} className="upload-icon" />
                    </div>

                    <h3 className="upload-title">Ladda upp CSV-fil</h3>
                    <p className="upload-subtitle">
                        Dra och släpp din fil här eller klicka för att välja fil
                    </p>

                    <button className="upload-btn" type="button">
                        <FileText size={16} /> Välj CSV-fil
                    </button>

                    <input
                        ref={fileInputRef}
                        className="file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                    />
                </div>

                {selectedFile && (
                    <div className="selected-file">
                        <div className="file-icon">
                            <FileText size={24} />
                        </div>
                        <div className="file-details">
                            <div className="file-name">{selectedFile.name}</div>
                            <div className="file-size">
                                {(selectedFile.size / 1024).toFixed(1)} KB
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="error-container">
                        <AlertTriangle size={20} />
                        <div>{error}</div>
                    </div>
                )}

                <div className="instructions-container">
                    <h4 className="instructions-title">
                        <FileText size={18} />
                        Instruktioner för CSV-fil
                    </h4>

                    <ul className="instructions-list">
                        <li className="instruction-item">
                            <div className="instruction-number">1</div>
                            <div>
                                Första raden i din CSV-fil bör innehålla kolumnnamn
                            </div>
                        </li>
                        <li className="instruction-item">
                            <div className="instruction-number">2</div>
                            <div>
                                För {config?.type === 'articles' ? 'artiklar' :
                                    config?.type === 'customers' ? 'kunder' :
                                        config?.type === 'suppliers' ? 'leverantörer' : 'data'} krävs följande fält: {' '}
                                <strong>
                                    {config?.requiredFields?.map(field => config.fieldsConfig[field]?.label).join(', ')}
                                </strong>
                            </div>
                        </li>
                        <li className="instruction-item">
                            <div className="instruction-number">3</div>
                            <div>
                                Varje rad representerar en {config?.type === 'articles' ? 'artikel' :
                                    config?.type === 'customers' ? 'kund' :
                                        config?.type === 'suppliers' ? 'leverantör' : 'post'}
                            </div>
                        </li>
                        <li className="instruction-item">
                            <div className="instruction-number">4</div>
                            <div>
                                Använd kommatecken (,) eller semikolon (;) som avgränsare
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
};

export default GenericImporterFileUploadStep;